import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Breadcrumb, Skeleton, message, Modal } from 'antd';
import { HeartOutlined, HeartFilled, PlayCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import httpClient from '../../utils/HttpClient';
import { COURSES_URL, COURSE_FAVORITE_URL } from '../../constants/Url';
import AuthModal from '../../components/auth/AuthModal';
import PROModal from '../../components/PROModal';
import { useSelector } from 'react-redux';
import VIPModal from '../../components/VIPModal';
import SuperVIPModal from '../../components/SuperVIPModal';
import { isDateExpiredUTC } from '../../utils/Utils';

export default function OnlineClassMemberPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.authReducer);
  const tutorId = location.state?.tutorId;

  // Returns true when memberType is 'trial' but the trial period has already ended
  const isTrialExpired = () => {
    const memberType = user?.user?.memberType;
    if (memberType !== 'trial') return false;
    const expiredTS = user?.user?.expiredTS;
    if (!expiredTS) return false;
    return isDateExpiredUTC(expiredTS);
  };

  const [loading, setLoading] = useState(true);
  const [isFavorited, setIsFavorited] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isVIPModalOpen, setIsVIPModalOpen] = useState(false);
  const [isPROModalOpen, setIsPROModalOpen] = useState(false);
  const [isSuperVIPModalOpen, setIsSuperVIPModalOpen] = useState(false);
  
  // Mock data matching pic 2 content
  const [courseData, setCourseData] = useState(null);

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    if (typeof seconds === 'string' && seconds.includes(':')) return seconds;
    const secs = parseInt(seconds, 10);
    if (isNaN(secs)) return '';
    const hours = Math.floor(secs / 3600);
    const minutes = Math.floor((secs % 3600) / 60);
    const remainingSeconds = secs % 60;
    const paddedMinutes = String(minutes).padStart(2, '0');
    const paddedSeconds = String(remainingSeconds).padStart(2, '0');
    if (hours > 0) return `${hours}:${paddedMinutes}:${paddedSeconds}`;
    return `${minutes}:${paddedSeconds}`;
  };

  const fetchCourse = async () => {
    try {
      setLoading(true);
      const res = await httpClient.get(`${COURSES_URL}/${id}`)
        .then(res => res.data)
        .catch(error => ({ status: 500, message: error?.message }));

      if (res.status === 200 && res.data) {
        const data = res.data;

        let videoCoverUrl = data.picUrl || data.coverImage || data.imageUrl;
        let extractedVideoUrl = data.videoIntro || data.videoName || "";

        if (!extractedVideoUrl && data.videoIntro) {
          if (data.picUrl && data.picUrl.includes('/public/')) {
            const domainAndPublic = data.picUrl.split('/public/')[0] + '/public/';
            const fileNameWithPrefix = data.picUrl.substring(data.picUrl.lastIndexOf('/') + 1);
            const originalVideoName = fileNameWithPrefix.split('_thumbnail.png')[0];
            extractedVideoUrl = domainAndPublic + originalVideoName;
          } else {
            extractedVideoUrl = data.videoIntro;
          }
        }

        setCourseData({
          ...data,
          id: data._id || data.id,
          title: data.title || "",
          category: data.categoryId?.name || "",
          price: data.price || 0,
          views: data.viewCount || 0,
          duration: formatDuration(data.videoDuration) || "",
          videoCover: videoCoverUrl,
          videoUrl: extractedVideoUrl,
          validityPeriod: data?.validityPeriod,
          tutor: {
            name: data.teacherId?.name || "",
            photo: data.teacherId?.picUrl || "",
            bio: data.teacherId?.textIntro || ""
          },
          courseIntro: data.intro || "",
          terms: data.priceTerms || ""
        });
        setIsFavorited(data.isFavorited || false);
      } else {
        message.error(t(res.message) || t('text_failed_loading_course'));
      }
    } catch (error) {
      console.error("Fetch course error", error);
    } finally {
      setLoading(false);
    }
  };

useEffect(() => {
  if (id) {
    fetchCourse();
  }
}, [id]);

  const handleToggleFavorite = async () => {
    try {
      const targetCourseId = id || courseData?.id || courseData?._id;
      if (!targetCourseId) {
        message.error(t('text_no_courseid'));
        return;
      }
      
      let res;
      if (isFavorited) {
        res = await httpClient.delete(COURSE_FAVORITE_URL, { data: { courseId: targetCourseId } })
          .then(r => r.data)
          .catch(e => ({ status: 500, message: e?.message }));
      } else {
        res = await httpClient.post(COURSE_FAVORITE_URL, { courseId: targetCourseId })
          .then(r => r.data)
          .catch(e => ({ status: 500, message: e?.message }));
      }

      if (res && res.status === 200) {
        setIsFavorited(!isFavorited);
        message.success(t(res.message) || "Success");
      } else {
        message.error(t(res?.message) || t("text_operation_failed"));
      }
    } catch (error) {
      console.error("handleToggleFavorite error", error);
      message.error(t("text_operation_failed"));
    }
  };

  const handlePlayVideo = async () => {
    const memberType = user?.user?.memberType;
    const hasActivePurchase = Boolean(courseData?.isActivePurchased || courseData?.isPurchased);

    try {
      message.loading({ content: t('text_loading_video'), key: 'videoLoading' });
      
      let endpoint = '';
      
      if (hasActivePurchase || memberType === 'VIP') {
        endpoint = `${COURSES_URL}/${courseData?.courseId || id}/video`;
      } else {
        endpoint = `${COURSES_URL}/${courseData?.courseId || id}/video-preview`;
      }

      if (endpoint) {
        const res = await httpClient.get(endpoint).then(r => r.data).catch(e => ({ status: 500, message: e?.message }));
        if (res.status === 200 && res.data) {
          message.success({ content: t('text_loading_success'), key: 'videoLoading', duration: 1 });
          const videoUrl = typeof res.data === 'string' ? res.data : (res.data.videoUrl || res.data.courseVideoUrl || res.data.url || res.data.videoPath || res.data.courseVideoName || ""); 
          setCourseData(prev => ({ ...prev, videoUrl: videoUrl || prev.videoUrl }));
          setIsVideoModalOpen(true);
        } else {
          // If video endpoint failed but courseData has preview videoUrl, fallback to it
          if (courseData?.videoUrl) {
            message.destroy('videoLoading');
            setIsVideoModalOpen(true);
          } else {
            message.error({ content: t(res?.message) || t('text_loading_video_failed'), key: 'videoLoading', duration: 2 });
            if ((memberType === 'vip' || memberType === 'pro') && !hasActivePurchase) {
              setIsVIPModalOpen(true);
            }
          }
        }
      } else if (courseData?.videoUrl) {
        message.destroy('videoLoading');
        setIsVideoModalOpen(true);
      }
    } catch (e) {
      if (courseData?.videoUrl) {
        message.destroy('videoLoading');
        setIsVideoModalOpen(true);
      } else {
        message.error({ content: t('text_network_error'), key: 'videoLoading', duration: 2 });
      }
    }
  };

  if (loading) {
    return (
      <div className="w-full min-h-screen pb-16 bg-[#fcfdfe]">
        <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          
          {/* Top Navigation Bar: Back Link & Breadcrumb Skeleton */}
          <div className="mb-6 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div 
              onClick={() => {
                if (window.history.state && window.history.state.idx > 0) {
                  navigate(-1);
                } else {
                  navigate(tutorId ? `/online-class/${tutorId}` : '/online-class');
                }
              }} 
              className="inline-flex items-center gap-2 cursor-pointer text-gray-500 hover:text-black transition-colors font-medium text-sm"
            >
              <ArrowLeftOutlined className="text-base" />
              <span>{t('text_back_previous_page')}</span>
            </div>
            <Skeleton.Input active size="small" style={{ width: '250px' }} />
          </div>

          {/* Hero Section Skeleton */}
          <div className="bg-white p-6 sm:p-8 rounded-xl flex flex-col lg:flex-row gap-8 shadow-sm border border-gray-100 mb-8">
            <div className="w-full lg:w-[60%] aspect-video relative rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center">
              <Skeleton.Image className="!w-full !h-full" active />
            </div>
            
            <div className="w-full lg:w-[40%] flex flex-col justify-between">
              <div>
                <div className="mb-3">
                  <Skeleton.Input active size="large" style={{ width: '80%', height: '36px' }} />
                </div>
                <div className="mb-6">
                  <Skeleton.Input active size="small" style={{ width: '100px' }} />
                </div>
                <div className="mb-4">
                  <Skeleton.Input active size="large" style={{ width: '150px' }} />
                </div>
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-100">
                <Skeleton.Button active style={{ width: '100%', height: '56px', borderRadius: '0.75rem' }} />
              </div>
            </div>
          </div>

          {/* Info Cards Section Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content Area */}
            <div className="lg:col-span-2 flex flex-col gap-8">
              <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100">
                <Skeleton.Input active size="default" style={{ width: '150px', marginBottom: '24px' }} />
                <Skeleton active paragraph={{ rows: 4 }} title={false} />
              </div>
              <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100">
                <Skeleton.Input active size="default" style={{ width: '150px', marginBottom: '24px' }} />
                <Skeleton active paragraph={{ rows: 3 }} title={false} />
              </div>
            </div>

            {/* Sidebar Area */}
            <div className="lg:col-span-1">
              <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100">
                <Skeleton.Input active size="default" style={{ width: '120px', marginBottom: '24px' }} />
                <div className="flex flex-col items-center">
                  <Skeleton.Avatar active size={100} shape="circle" style={{ marginBottom: '16px' }} />
                  <Skeleton.Input active size="small" style={{ width: '100px', marginBottom: '12px' }} />
                  <Skeleton active paragraph={{ rows: 2 }} title={false} />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen pb-16 bg-[#fcfdfe]">
      <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        
        {/* Top Navigation Bar: Back Link & Breadcrumb */}
        <div className="mb-6 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div 
            onClick={() => {
              if (window.history.state && window.history.state.idx > 0) {
                navigate(-1);
              } else {
                navigate(tutorId ? `/online-class/${tutorId}` : '/online-class');
              }
            }} 
            className="inline-flex items-center gap-2 cursor-pointer text-gray-500 hover:text-black transition-colors font-medium text-sm"
          >
            <ArrowLeftOutlined className="text-base" />
            <span>{t('text_back_previous_page')}</span>
          </div>

          <Breadcrumb separator=">">
            <Breadcrumb.Item onClick={() => navigate('/')} className="cursor-pointer hover:text-black">{t('text_homepage')}</Breadcrumb.Item>
            <Breadcrumb.Item onClick={() => navigate('/education')} className="cursor-pointer hover:text-black">{t('text_tech_edu')}</Breadcrumb.Item>
            <Breadcrumb.Item 
              onClick={() => navigate(tutorId ? `/online-class/${tutorId}` : '/online-class')} 
              className="cursor-pointer hover:text-black"
            >
              {t('menu_online_class')}
            </Breadcrumb.Item>
            <Breadcrumb.Item>{courseData.title}</Breadcrumb.Item>
          </Breadcrumb>
        </div>

        {/* Hero Section */}
        <div className="bg-white p-6 sm:p-8 rounded-xl flex flex-col lg:flex-row gap-8 shadow-sm border border-gray-100 mb-8">
          
          {/* Left: Video Cover */}
          <div className="w-full lg:w-[60%] aspect-video relative rounded-lg overflow-hidden bg-black flex items-center justify-center group">
            <img src={courseData.videoCover} alt={courseData.title} className="w-full h-full object-cover opacity-80" />
            
            {/* Play Button Overlay */}
            <div 
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              onClick={handlePlayVideo}
            >
              <PlayCircleOutlined className="text-white text-6xl opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" />
            </div>

            {/* Video Meta info */}
            <div className="absolute bottom-4 left-4 right-4 flex justify-between items-center text-white text-sm">
              <span>{courseData.views} {t('text_play_no')}</span>
              <span className="bg-black/60 px-2 py-1 rounded text-xs">{courseData.duration}</span>
            </div>
            
            {/* Favorite Icon */}
            <div 
              className="absolute top-4 right-4 w-10 h-10 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center cursor-pointer hover:bg-black/60 transition-colors"
              onClick={handleToggleFavorite}
            >
              {isFavorited ? (
                <HeartFilled className="text-red-500 text-xl" />
              ) : (
                <HeartOutlined className="text-white text-xl" />
              )}
            </div>
          </div>

          {/* Right: Course Details */}
          <div className="w-full lg:w-[40%] flex flex-col justify-between">
            <div>
              <div className="flex gap-3 items-start justify-between mb-3">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 m-0 leading-tight">
                  {courseData.title}
                </h1>
              </div>
              
              <span className="inline-block bg-pink-100 text-[#e91e63] text-sm px-3 py-1 rounded font-medium mb-6">
                {courseData.category}
              </span>

              <div className="flex items-baseline gap-4 mb-4">
                <span className="text-[#e91e63] text-4xl font-black">&yen; {courseData.price}</span>
                <span className="text-gray-400 text-sm">{t("text_member_can_buy")} {courseData?.validityPeriod} {t("text_day_no")}</span>
              </div>
            </div>
            <div className='p-[2px] sm:w-[240px] bg-gradient-to-r from-[#50c7f8] via-[#7887fd] to-[#df97fb] shadow-md'>
              <button
                onClick={() => {
                  const memberType = user?.user?.memberType;
                  const hasActivePurchase = Boolean(courseData?.isActivePurchased || courseData?.isPurchased);

                  if (memberType === "VIP" || hasActivePurchase) {
                    // VIP member or already purchased — play directly
                    handlePlayVideo();
                  } else if (!memberType || memberType === 'normal' || (memberType === 'trial' && isTrialExpired())) {
                    // No active membership (normal) or expired free trial — must subscribe first
                    setIsPROModalOpen(true);
                  } 
                  else {
                    // Active trial or pro member — can purchase the course
                    setIsVIPModalOpen(true);
                  }
                }}
                className="flex items-center justify-center gap-2 px-5 py-2.5 text-white bg-gradient-to-r from-[#21163e] to-[#252554] hover:opacity-90 transition-all h-full w-full cursor-pointer"
              >
                <span className="text-sm tracking-widest text-center">
                  {user?.user?.memberType === "VIP" || courseData?.isActivePurchased || courseData?.isPurchased ? t("text_learn_now") : t("text_enroll_now")}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Info Cards Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content Area (2/3 width) */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            {/* Course Intro */}
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="inline-block"></span>
                {t('head_course_details')}
              </h2>
              <p className="text-gray-600 leading-relaxed text-[15px]">
                {courseData.courseIntro}
              </p>
            </div>

            {/* Terms */}
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="inline-block"></span>
                {t('head_price')}
              </h2>
              <p className="text-gray-600 leading-relaxed text-[15px]">
                {courseData.terms}
              </p>
            </div>
          </div>

          {/* Sidebar Area (1/3 width) */}
          <div className="lg:col-span-1">
            {/* Tutor Info */}
            <div className="bg-white p-6 sm:p-8 rounded-xl shadow-sm border border-gray-100 sticky top-24">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                <span className="inline-block"></span>
                {t('head_teacher')}
              </h2>
              
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden mb-4 shadow-md border-4 border-white">
                  <img src={courseData.tutor.photo} alt={courseData.tutor.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{courseData.tutor.name}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {courseData.tutor.bio}
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Video Player Modal */}
      <Modal
        open={isVideoModalOpen}
        onCancel={() => setIsVideoModalOpen(false)}
        footer={null}
        destroyOnClose
        width={800}
        closeIcon={
          <span style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            width: 32, 
            height: 32, 
            borderRadius: '50%', 
            backgroundColor: 'rgba(0, 0, 0, 0.7)', 
            color: '#ffffff', 
            fontSize: 16, 
            border: '1px solid rgba(255, 255, 255, 0.5)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
            marginTop: -6,
            marginRight: -6
          }}>
            ✕
          </span>
        }
        styles={{ 
          body: { padding: 0, backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', overflow: 'hidden' } 
        }}
        centered
      >
        <video 
          className="w-full aspect-video rounded-lg" 
          controls 
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          autoPlay 
          src={courseData?.videoUrl}
        >
          {t("text_tag_not_support")}
        </video>
      </Modal>

      {/* VIP Subscription Modal */}
      {
        user?.user?.memberType === "normal" || isPROModalOpen ?
          <PROModal
            open={isVIPModalOpen || isPROModalOpen}
            onCancel={() => {
              setIsVIPModalOpen(false);
              setIsPROModalOpen(false);
            }}
            fetchCourse={fetchCourse}
          /> : ""
      }
      {
        (user?.user?.memberType === "pro" || user?.user?.memberType === "trial") && !isPROModalOpen && !isSuperVIPModalOpen ?
          <VIPModal
            courseId={courseData?.courseId}
            open={isVIPModalOpen}
            onCancel={() => setIsVIPModalOpen(false)}
            onConfirm={() => message.info(t('text_Purchase_integrated'))}
            onUpgrade={() => {
              setIsVIPModalOpen(false);
              setIsSuperVIPModalOpen(true);
            }}
            coursePrice={courseData.price}
            fetchCourse={fetchCourse}
          /> : ""
      }
      
      {/* Super VIP Modal */}
      <SuperVIPModal
        open={isSuperVIPModalOpen}
        onCancel={() => setIsSuperVIPModalOpen(false)}
        fetchCourse={fetchCourse}
      />
    </div>
  );
}
